import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { Refresh, Edit, Save, Add } from '@mui/icons-material';
import axios from 'axios';
import { format, subYears, parse } from 'date-fns';

const UserPayment = ({ userID }) => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(subYears(new Date(), 1));
  const [toDate, setToDate] = useState(new Date());
  const [searchText, setSearchText] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editForm, setEditForm] = useState({ pDate: '', amount: '' });
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newPaymentForm, setNewPaymentForm] = useState({ pDate: new Date(), amount: '' });

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    setSnackbarOpen(false);
    try {
      const formattedFrom = format(fromDate, 'yyyy-MM-dd');
      const formattedTo = format(toDate, 'yyyy-MM-dd');
      const payload = {
        userID: userID,
        startDate: formattedFrom,
        endDate: formattedTo,
      };
      const apiUrl = 'https://copyapi.avangar.ir/api/forex/PaymentList';
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      if (!response.data || response.data.length === 0) {
        setPayments([]);
        setSnackbarMessage(t('payments.noData'));
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      } else {
        setPayments(response.data);
      }
    } catch (err) {
      console.error('UserPayment error:', err);
      let errorMessage = err.response?.data?.Message || t('payments.error');
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [userID, fromDate, toDate]);

  const totalAmount = useMemo(() => {
    return payments.reduce((sum, payment) => sum + (payment.Amount || 0), 0).toFixed(2);
  }, [payments]);

  const handleRefresh = () => {
    fetchPayments();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEditClick = (payment) => {
    setEditingPaymentId(payment.UserPaymentID);
    setEditForm({
      pDate: parse(payment.PaymentDate, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
      amount: payment.Amount.toString(),
    });
  };

  const handleSaveClick = async (paymentId) => {
    try {
      const payload = {
        ID: paymentId,
        pDate: format(editForm.pDate, 'yyyy-MM-dd'),
        amount: parseFloat(editForm.amount),
      };
      const apiUrl = 'https://copyapi.avangar.ir/api/forex/PaymentEdit';
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      if (response.status === 200) {
        setPayments((prevPayments) =>
          prevPayments.map((payment) =>
            payment.UserPaymentID === paymentId
              ? {
                  ...payment,
                  PaymentDate: format(editForm.pDate, "yyyy-MM-dd'T'HH:mm:ss"),
                  Amount: parseFloat(editForm.amount),
                }
              : payment
          )
        );
        setEditingPaymentId(null);
        setEditForm({ pDate: '', amount: '' });
        setSnackbarMessage(t('payments.saved'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('PaymentEdit error:', err);
      setSnackbarMessage(t('payments.saveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleAddNewClick = () => {
    setIsAddingNew(true);
    setNewPaymentForm({ pDate: new Date(), amount: '' });
  };

  const handleNewPaymentSave = async () => {
    try {
      const payload = {
        userid: userID,
        pDate: format(newPaymentForm.pDate, 'yyyy-MM-dd'),
        amount: parseFloat(newPaymentForm.amount),
      };
      const apiUrl = 'https://copyapi.avangar.ir/api/forex/PaymentInsert';
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      if (response.status === 200) {
        setIsAddingNew(false);
        setNewPaymentForm({ pDate: new Date(), amount: '' });
        setSnackbarMessage(t('payments.inserted'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchPayments();
      }
    } catch (err) {
      console.error('PaymentInsert error:', err);
      setSnackbarMessage(t('payments.insertError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewPaymentFormChange = (field, value) => {
    setNewPaymentForm((prev) => ({ ...prev, [field]: value }));
  };

  const filteredPayments = useMemo(() => {
    if (!searchText.trim()) return payments;
    const searchLower = searchText.toLowerCase();
    const filtered = payments.filter((payment) => {
      const fieldsToSearch = [
        payment.PaymentDate || '',
        String(payment.Amount || ''),
      ];
      return fieldsToSearch.some((field) => field.toLowerCase().includes(searchLower));
    });
    if (filtered.length === 0 && searchText.trim()) {
      setSnackbarMessage(t('payments.noResults'));
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
    return filtered;
  }, [payments, searchText, t]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: '100%', mx: 'auto', p: 2 }}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DatePicker
              label={t('payments.fromDate')}
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              format="yyyy-MM-dd"
              slotProps={{
                textField: { size: 'small' },
              }}
              sx={{ width: '152.95px' }}
            />
            <DatePicker
              label={t('payments.toDate')}
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              format="yyyy-MM-dd"
              slotProps={{
                textField: { size: 'small' },
              }}
              sx={{ width: '152.95px' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label={t('payments.search')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{ width: '168px' }}
            />
            <IconButton onClick={handleRefresh} aria-label={t('payments.refresh')}>
              <Refresh />
            </IconButton>
            <Typography
              variant="body1"
              sx={{ color: 'error.main', fontWeight: 'bold', mr: 2 }}
            >
              {t('payments.totalAmount')}: {totalAmount}
            </Typography>
            <IconButton onClick={handleAddNewClick} aria-label={t('payments.addNew')}>
              <Add />
            </IconButton>
          </Box>
        </Paper>
        <Paper
          sx={{
            maxHeight: '400px',
            overflowY: 'auto',
            p: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: 2,
          }}
        >
          {isAddingNew && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: '#f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <DatePicker
                  label={t('payments.date')}
                  value={newPaymentForm.pDate}
                  onChange={(newValue) => handleNewPaymentFormChange('pDate', newValue)}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: { size: 'small', sx: { mb: 1 } },
                  }}
                />
                <TextField
                  label={t('payments.amount')}
                  value={newPaymentForm.amount}
                  onChange={(e) => {
                    if (/^\d*\.?\d*$/.test(e.target.value)) {
                      handleNewPaymentFormChange('amount', e.target.value);
                    }
                  }}
                  size="small"
                  sx={{ mb: 1 }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*\\.?[0-9]*' }}
                />
              </Box>
              <Box>
                <IconButton
                  onClick={handleNewPaymentSave}
                  aria-label={t('payments.save')}
                  disabled={!newPaymentForm.amount || !newPaymentForm.pDate}
                >
                  <Save />
                </IconButton>
              </Box>
            </Box>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : filteredPayments.length > 0 ? (
            filteredPayments.slice(0, 10).map((payment) => (
              <Box
                key={payment.UserPaymentID}
                sx={{
                  mt: 1,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  bgcolor: '#f9f9f9',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  {editingPaymentId === payment.UserPaymentID ? (
                    <>
                      <DatePicker
                        label={t('payments.date')}
                        value={editForm.pDate}
                        onChange={(newValue) => handleEditFormChange('pDate', newValue)}
                        format="yyyy-MM-dd"
                        slotProps={{
                          textField: { size: 'small', sx: { mb: 1 } },
                        }}
                      />
                      <TextField
                        label={t('payments.amount')}
                        value={editForm.amount}
                        onChange={(e) => {
                          if (/^\d*\.?\d*$/.test(e.target.value)) {
                            handleEditFormChange('amount', e.target.value);
                          }
                        }}
                        size="small"
                        sx={{ mb: 1 }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*\\.?[0-9]*' }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="body2">
                        <strong>{t('payments.date')}:</strong> {payment.PaymentDate || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t('payments.amount')}:</strong> {payment.Amount || '0'}
                      </Typography>
                    </>
                  )}
                </Box>
                <Box>
                  <IconButton
                    onClick={() => handleEditClick(payment)}
                    aria-label={t('payments.edit')}
                    disabled={editingPaymentId !== null || isAddingNew}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    onClick={() => handleSaveClick(payment.UserPaymentID)}
                    aria-label={t('payments.save')}
                    disabled={editingPaymentId !== payment.UserPaymentID}
                  >
                    <Save />
                  </IconButton>
                </Box>
              </Box>
            ))
          ) : (
            <Typography>{t('payments.noData')}</Typography>
          )}
        </Paper>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              animation: 'fadeInDown 0.5s ease-out forwards',
            },
          }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              bgcolor: snackbarSeverity === 'success' ? '#e8f5e9' : '#ffebee',
              color: snackbarSeverity === 'success' ? '#2e7d32' : '#c62828',
              '& .MuiAlert-icon': {
                color: snackbarSeverity === 'success' ? '#4caf50' : '#d32f2f',
              },
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default UserPayment;